import { zodResolver } from "@hookform/resolvers/zod";
import type { ComponentProps } from "react";
import type { UseFormReturn } from "react-hook-form";
import { useForm } from "react-hook-form";
import type { Paths } from "type-fest";
import type { z } from "zod";

import { ReviewEntity, ReviewServicePorts, StarRatingScale } from "~/entities/review";
import { Tk, TranslationParams, useTranslation2 } from "~/shared/i18n";
import { EMPTY, SPACE } from "~/shared/str";
import { cn } from "~/shared/ui";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "~/shared/ui/form";
import { Input } from "~/shared/ui/input";
import { PhoneInput } from "~/shared/ui/phone-input";
import { Textarea } from "~/shared/ui/textarea";
import { DEFAULT_ZOD_PARSE_PARAMS, errorMapForForms2 } from "~/shared/zod";

const zSourceSchema = ReviewServicePorts.ProviderIos.Create.zIn;
type SourceSchema = z.infer<typeof zSourceSchema>;

const zSchema = zSourceSchema.extend({
  orderNumber: ReviewEntity.zOrderNumberAsString,
});
type Schema = z.infer<typeof zSchema>;

type Props = Omit<ComponentProps<"form">, "onSubmit"> & {
  onSubmit?: Parameters<UseFormReturn<SourceSchema>["handleSubmit"]>[0] | undefined;
};

function CreationForm({ className, onSubmit, ...props }: Props) {
  const {
    translation: { t },
    postproc: { tc },
  } = useTranslation2();

  const form = useForm({
    resolver: zodResolver(zSchema, {
      ...DEFAULT_ZOD_PARSE_PARAMS,
      errorMap: errorMapForForms2(t),
    }),
    defaultValues: {
      rating: 1,
      pros: EMPTY,
      cons: EMPTY,
      text: EMPTY,
      customer: {
        firstName: EMPTY,
        lastName: EMPTY,
        email: EMPTY,
        phone: EMPTY,
      },
      orderNumber: EMPTY,
    },
  });

  const handleSubmit: Parameters<(typeof form)["handleSubmit"]>[0] = ({ orderNumber, ...data }) => {
    onSubmit?.({
      ...data,
      orderNumber: ReviewEntity.convertFromOrderNumberAsString(orderNumber),
    });
  };

  return (
    <Form {...form}>
      <form
        className={cn("flex flex-col gap-4", className)}
        onSubmit={(event) => void form.handleSubmit(handleSubmit)(event)}
        {...props}
      >
        <FormField
          control={form.control}
          name="rating"
          render={({ field: { value, onChange } }) => (
            <FormItem>
              <FormLabel>{tc(Tk.RATING)}</FormLabel>
              <FormControl>
                <StarRatingScale
                  rating={(value ?? 1) as ReviewEntity.RatingSchema}
                  isEditable
                  onRatingChange={onChange}
                  starProps={{
                    className: "size-6 sm:size-8",
                  }}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <div className="flex flex-col items-start gap-4 sm:flex-row *:sm:w-1/2">
          {(
            [
              {
                name: "customer.firstName",
                labelT: Tk.FIRST_NAME,
              },
              {
                name: "customer.lastName",
                labelT: Tk.LAST_NAME,
              },
            ] satisfies {
              name: Paths<Schema>;
              labelT: TranslationParams.Type;
            }[]
          ).map(({ name, labelT }) => (
            <FormField
              key={name}
              control={form.control}
              name={name}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{tc(...TranslationParams.toTuple(labelT))}</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          ))}
        </div>
        {(
          [
            {
              name: "pros",
              labelTk: Tk.PROS,
            },
            {
              name: "cons",
              labelTk: Tk.CONS,
            },
            {
              name: "text",
              labelTk: Tk.TEXT,
            },
          ] satisfies {
            name: keyof Schema;
            labelTk: Tk;
          }[]
        ).map(({ name, labelTk }) => (
          <FormField
            key={name}
            control={form.control}
            name={name}
            render={({ field }) => (
              <FormItem>
                <FormLabel>{tc(labelTk)}</FormLabel>
                <FormControl>
                  <Textarea {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        ))}
        <FormField
          control={form.control}
          name="orderNumber"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                {[tc(Tk.ORDER_NUMBER), ["(", t(Tk.OPTIONAL), ")"].join(EMPTY)].join(SPACE)}
              </FormLabel>
              <FormControl>
                <Input {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <fieldset className="flex flex-col gap-4 rounded-md border p-6">
          <legend>
            {tc(Tk.CONTACT, {
              count: 2,
            })}
          </legend>
          <div>
            <p className="italic">
              {t(Tk.PLEASE_PROVIDE_THIS_INFORMATION_ONLY_IF_YOU_WOULD_LIKE_US_TO_CONTACT_YOU)}
            </p>
            <p className="italic">{t(Tk.THIS_INFORMATION_WILL_NOT_BE_AVAILABLE_PUBLICLY)}</p>
          </div>
          <div className="flex flex-col items-start gap-4 sm:flex-row *:sm:w-1/2">
            {(
              [
                {
                  name: "customer.email",
                  labelT: Tk.EMAIL,
                },
                {
                  name: "customer.phone",
                  labelT: Tk.PHONE,
                },
              ] satisfies {
                name: Paths<Schema>;
                labelT: TranslationParams.Type;
              }[]
            ).map(({ name, labelT }) => (
              <FormField
                key={name}
                control={form.control}
                name={name}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{tc(...TranslationParams.toTuple(labelT))}</FormLabel>
                    <FormControl>
                      {name === "customer.phone" ? <PhoneInput {...field} /> : <Input {...field} />}
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            ))}
          </div>
        </fieldset>
      </form>
    </Form>
  );
}

export { CreationForm };
